import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import toast, { Toaster } from "react-hot-toast";

import Layout from "../reusable-components/Layout";
import { useAppContext } from "../context/ThemeContext";
import ClipLoader from "react-spinners/ClipLoader";
import firebase, { database } from "../config";

function PostForm() {
	const { siteTheme, state } = useAppContext();
	const [sending, setSending] = React.useState(false);
	const [posted, setPosted] = React.useState(false);

	let isDay = state.isDay;
	if (JSON.parse(sessionStorage.getItem("isDay"))) {
		isDay = JSON.parse(sessionStorage.getItem("isDay"));
	}

	const PostSchema = Yup.object().shape({
		activeDuration: Yup.number().required("Required"),
		imageurl: Yup.string().url("Invalid URL format").required("Required"),
		postBody: Yup.string().min(10, "Too Short!").required("Required"),
		readTime: Yup.string().min(5, "Too Short!").required("Required"),
		language: Yup.string().min(3, "Too Short!").required("Required"),
		title: Yup.string().min(10, "Too Short!").required("Required"),
		linkedin: Yup.string().min(5, "Too Short!").required("Required"),
		linkedinurl: Yup.string().url("Invalid URL format").required("Required"),
		youtube: Yup.string().min(5, "Too Short!").required("Required"),
		youtubeurl: Yup.string().url("Invalid URL format").required("Required"),
	});

	const override = {
		display: "block",
		margin: "0 auto",
		borderColor: isDay ? "text-brandwhite" : "text-deepGrey",
	};

	async function handleRequest(value) {
		setSending(true);

		try {
			const post = {
				image: value?.imageurl,
				language: [...value?.language?.split(", ")],
				paragraph: value?.postBody,
				socials: [
					{
						socialName: value?.linkedin,
						url: value?.linkedinurl,
					},
					{
						socialName: value?.youtube,
						url: value?.youtubeurl,
					},
				],
				title: value?.title,
			};

			await database.collection("posts").add({
				publishDate: firebase.firestore.FieldValue.serverTimestamp(),
				newFor: +value?.activeDuration,
				post: post,
				readTime: value?.readTime,
			});

			setSending(false);
			setPosted(true);
			toast("Blog Posted!");
		} catch (error) {
			console.error("Upload failed:", error);
			setSending(false);
		}
	}

	useEffect(() => {
		const postedId = setTimeout(() => {
			if (posted) {
				setPosted(false);
			}
		}, 3000);

		return () => clearTimeout(postedId);
	}, [posted]);

	return (
		<>
			<Helmet>
				<title>Wiingr | Post</title>
			</Helmet>
			<Layout>
				<div
					className={`${siteTheme.textColor} ease-in duration-300 relative flex flex-col w-full h-[100vh] px-4 mx-auto max-w-max-content-width pb-20 overflow-y-scroll overflow-x-hidden no-scrollbar pt-24 font-saira`}>
					<div
						className={`${siteTheme.textColor} w-full h-[100%] md:h-full flex-1 overflow-scroll gap-y-2 no-scrollbar border-[0.5px] border-neutral-700 rounded-2xl bg-[rgba(250,250,250,0.01)] px-1 pb-4 `}>
						<div className="py-2">
							<p className="text-xl text-center">--- Blog Post ---</p>
						</div>
						<Formik
							initialValues={{
								title: "",
								activeDuration: "",
								readTime: "",
								imageurl: "",
								language: "",
								linkedin: "",
								linkedinurl: "",
								youtube: "",
								youtubeurl: "",
								postBody: "",
							}}
							validationSchema={PostSchema}
							onSubmit={async (values, { resetForm }) => {
								handleRequest(values);
								resetForm();
							}}>
							{({ errors, touched }) => (
								<Form className="flex flex-col gap-y-1">
									<div className="pt-3">
										<p className="text-sm text-center">Post metadata</p>
									</div>
									<div className="grid grid-cols-1 gap-1 xs:grid-cols-4">
										<Field
											id="activeDuration"
											name="activeDuration"
											placeholder="--- Active duration ---"
											type="number"
											className={`flex-[0.9] p-2 border-[0.5px] placeholder:text-center focus:ring-0 focus:border-neutral-600 border-neutral-700 rounded-xl bg-[rgba(250,250,250,0.02)] text-sm ${
												errors.activeDuration && touched.activeDuration
													? "border-red-900"
													: "border-neutral-700"
											}`}
										/>
										<Field
											id="readTime"
											name="readTime"
											placeholder="--- Read time ---"
											type="text"
											className={`flex-[0.9] p-2 border-[0.5px] placeholder:text-center focus:ring-0 focus:border-neutral-600 border-neutral-700 rounded-xl bg-[rgba(250,250,250,0.02)] text-sm ${
												errors.readTime && touched.readTime
													? "border-red-900"
													: "border-neutral-700"
											}`}
										/>
										<Field
											id="imageurl"
											name="imageurl"
											placeholder="--- Image url ---"
											type="text"
											className={`p-2 border-[0.5px] placeholder:text-center focus:ring-0 focus:border-neutral-600 border-neutral-700 rounded-xl bg-[rgba(250,250,250,0.02)] text-sm ${
												errors.imageurl && touched.imageurl
													? "border-red-900"
													: "border-neutral-700"
											}`}
										/>
										<Field
											id="language"
											name="language"
											placeholder="--- Lang/Framework/Lib ---"
											type="text"
											className={`p-2 border-[0.5px] placeholder:text-center focus:ring-0 focus:border-neutral-600 border-neutral-700 rounded-xl bg-[rgba(250,250,250,0.02)] text-sm ${
												errors.language && touched.language
													? "border-red-900"
													: "border-neutral-700"
											}`}
										/>
									</div>
									<div className="grid grid-cols-1 gap-1 xs:grid-cols-4">
										<Field
											id="linkedin"
											name="linkedin"
											placeholder="--- Linkedin ---"
											type="text"
											className={`p-2 border-[0.5px] placeholder:text-center focus:ring-0 focus:border-neutral-600 border-neutral-700 rounded-xl bg-[rgba(250,250,250,0.02)] text-sm ${
												errors.linkedin && touched.linkedin
													? "border-red-900"
													: "border-neutral-700"
											}`}
										/>
										<Field
											id="linkedinurl"
											name="linkedinurl"
											placeholder="--- linkedin url ---"
											type="text"
											className={`p-2 border-[0.5px] placeholder:text-center focus:ring-0 focus:border-neutral-600 border-neutral-700 rounded-xl bg-[rgba(250,250,250,0.02)] text-sm ${
												errors.linkedinurl && touched.linkedinurl
													? "border-red-900"
													: "border-neutral-700"
											}`}
										/>
										<Field
											id="youtube"
											name="youtube"
											placeholder="--- youtube ---"
											type="text"
											className={`p-2 border-[0.5px] placeholder:text-center focus:ring-0 focus:border-neutral-600 border-neutral-700 rounded-xl bg-[rgba(250,250,250,0.02)] text-sm ${
												errors.youtube && touched.youtube
													? "border-red-900"
													: "border-neutral-700"
											}`}
										/>
										<Field
											id="youtubeurl"
											name="youtubeurl"
											placeholder="--- youtube url ---"
											type="text"
											className={`p-2 border-[0.5px] placeholder:text-center focus:ring-0 focus:border-neutral-600 border-neutral-700 rounded-xl bg-[rgba(250,250,250,0.02)] text-sm ${
												errors.youtubeurl && touched.youtubeurl
													? "border-red-900"
													: "border-neutral-700"
											}`}
										/>
									</div>

									<div className="pt-3">
										<p className="text-sm text-center">Post content</p>
									</div>

									<Field
										id="title"
										name="title"
										placeholder="--- title ---"
										type="text"
										className={`flex-[0.9] p-2 border-[0.5px] placeholder:text-center focus:ring-0 focus:border-neutral-600 border-neutral-700 rounded-xl bg-[rgba(250,250,250,0.02)] text-sm ${
											errors.title && touched.title
												? "border-red-900"
												: "border-neutral-700"
										}`}
									/>
									<Field name="postBody">
										{({
											field, // { name, value, onChange, onBlur }
											form: { touched, errors }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
											meta,
										}) => (
											<div
												className={`flex flex-row gap-1 columns-1 sm:columns-2 border-[0.5px] p-1 border-neutral-700 rounded-xl ${
													meta.touched && meta.error
														? "border-red-900"
														: "border-neutral-700"
												}`}>
												<textarea
													type="text"
													placeholder="--- Post content ? ---"
													row={100}
													{...field}
													className={`p-2 ease-in duration-300 w-full h-[500px] placeholder:text-center focus:ring-0 focus:border-neutral-600 border-none rounded-xl bg-[rgba(250,250,250,0.02)] text-sm`}
												/>
											</div>
										)}
									</Field>

									{sending ? (
										<ClipLoader
											color={isDay ? "text-brandBlack" : "text-lightGrey"}
											loading={sending}
											cssOverride={override}
											size={20}
											aria-label="Loading Spinner"
											data-testid="loader"
										/>
									) : (
										<div className="w-full mx-auto">
											<button
												disabled={false}
												type="submit"
												className={`py-2 w-full px-5 rounded-lg duration-300 ease-out ${
													isDay
														? "bg-brandBlack text-lightGrey hover:bg-lightGrey hover:text-brandBlack"
														: "bg-lightGrey text-brandBlack hover:bg-brandBlack hover:text-lightGrey"
												}`}>
												Post
											</button>
										</div>
									)}

									<Toaster />
								</Form>
							)}
						</Formik>
					</div>
				</div>
			</Layout>
		</>
	);
}

export default PostForm;
